<template>
  <div class="d-flex flex-column ga-4 mb-4">
    <div class="d-flex flex-wrap ga-4 justify-space-between">
      <v-chip color="primary" size="large">
        <v-icon icon="mdi-bookmark-check" />&nbsp;
        {{ merchantsStore.currentMerchant?.legalEntityName }}
      </v-chip>
      <v-btn v-if="auth.isStaff" color="error" to="/company-settings/staff">
        {{ t("companies.settings.staffSettingsButton") }}
      </v-btn>
    </div>

    <BillingEmail v-if="auth.hasCompanyScoped(Permission.Company_UpdateDetails)" />

    <v-expansion-panels v-if="canDeactivate">
      <v-expansion-panel
        expand-icon="mdi-store-remove-outline"
        collapse-icon="mdi-store-remove-outline"
        @group:selected="({ value }) => value && stepDeactivationFlow('expand')"
      >
        <v-expansion-panel-title>
          <span class="text-error">{{ t("companies.settings.closeAccount.title") }}</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <div v-if="customerInContract">
            <p>
              {{
                t("companies.settings.closeAccount.inContractMessage", { email: "help@yetipay.me" })
              }}
            </p>
          </div>

          <div v-else>
            <p>{{ t("companies.settings.closeAccount.noContractMessage") }}</p>
            <p class="mt-4 d-flex justify-end">
              <v-btn
                color="error"
                :loading="loading"
                prepend-icon="mdi-delete"
                @click="stepDeactivationFlow('dialog', () => (isDialogActive = true))"
              >
                {{ t("form.close") }}
              </v-btn>
            </p>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-dialog v-model="isDialogActive" max-width="500px">
      <v-card>
        <template #title>
          <h3 v-if="currentStep === Step.Final" class="text-error">
            {{ t("companies.settings.closeAccount.dialog.closedTitle") }}
          </h3>
          <h3 v-else class="text-error">
            {{ t("companies.settings.closeAccount.dialog.title") }}
          </h3>
        </template>
        <template #text>
          <v-window v-model="currentStep">
            <v-window-item :value="Step.Confirm">
              <p>
                <v-combobox
                  v-model="reason"
                  :items="[...possibleChurnReasons, 'Other']"
                  :label="t('companies.settings.closeAccount.dialog.reasonLabel')"
                />
                <transition>
                  <v-text-field
                    v-if="reason === 'Other'"
                    v-model="otherReason"
                    :label="t('companies.settings.closeAccount.dialog.otherReasonLabel')"
                  />
                </transition>
              </p>
              <br />
              <p>{{ t("companies.settings.closeAccount.dialog.warningMessage") }}</p>
              <br />
              <p>{{ t("companies.settings.closeAccount.dialog.balanceMessage") }}</p>
            </v-window-item>

            <v-window-item :value="Step.Final">
              <p>{{ t("companies.settings.closeAccount.dialog.closedMessage") }}</p>
              <br />
              <p>{{ t("companies.settings.closeAccount.dialog.returnMessage") }}</p>
              <p>
                <a href="https://www.royalmail.com/track-my-return#/details/5864" target="_blank"
                  >https://www.royalmail.com/track-my-return#/details/5864</a
                >
              </p>
              <br />
              <p>
                <b>{{ t("companies.settings.closeAccount.dialog.liabilityMessage") }}</b>
              </p>
            </v-window-item>
          </v-window>
        </template>
        <template #actions>
          <v-window v-model="currentStep" :touch="false" class="d-flex flex-fill">
            <v-window-item
              :value="Step.Confirm"
              :class="currentStep === Step.Confirm ? 'd-flex' : ''"
            >
              <v-btn-nd
                :text="t('form.cancel')"
                class="ms-4 my-4"
                @click="isDialogActive = false"
              />
              <v-spacer />
              <v-btn
                color="error"
                class="bg-error on-error me-4 my-4"
                :text="t('form.continue')"
                :loading="loading"
                :disabled="!realReason"
                @click="stepDeactivationFlow('confirm', () => (currentStep = Step.Final))"
              />
            </v-window-item>

            <v-window-item :value="Step.Final" class="d-flex">
              <v-spacer />
              <v-btn
                class="bg-primary on-primary me-4 my-4"
                :text="t('form.done')"
                @click="closeFinalStep"
              />
            </v-window-item>
          </v-window>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { DealRates } from "@tableyeti/merchant-service";
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useYetipayApi } from "@/composables/useYetipayApi";
import { useAuthorizationStore, Permission } from "@/store/authorization";
import { useMerchantsStore } from "@/store/merchants";

const { t } = useI18n();
const { api, currentMerchantId } = useYetipayApi();
const auth = useAuthorizationStore();
const merchantsStore = useMerchantsStore();

enum Step {
  Confirm = "confirm",
  Final = "final",
}
const possibleChurnReasons = [
  "Yetipay is missing one or more features that I need",
  "Billing issue",
  "I can get better rates elsewhere",
  "I'm moving to cash only",
  "My business is closing down",
  "Technical issues with yetipay devices",
  "I only needed a card machine temporarily",
];

const canDeactivate = computed(
  () =>
    (!merchantsStore.currentMerchant ||
      !merchantsStore.hasMerchantChurned(merchantsStore.currentMerchant)) &&
    auth.hasCompanyScoped(Permission.Company_Deactivate) &&
    auth.hasCompanyScoped(Permission.Company_GetRates),
);
const customerInContract = computed<boolean>(
  () => (deal.value?.contractEndDate ?? "") > new Date().toISOString(),
);
const isDialogActive = ref(false);
const reason = ref("");
const otherReason = ref("");
const realReason = computed(() => (reason.value === "Other" ? otherReason.value : reason.value));
const currentStep = ref(Step.Confirm);
const loading = ref(false);
const deal = ref<DealRates | undefined>(undefined);

onMounted(async () => {
  if (!canDeactivate.value) {
    return;
  }
  deal.value = await api!.merchantGetRates(currentMerchantId).then((res) => res.data);
});

const stepDeactivationFlow = async (
  step: "expand" | "dialog" | "confirm",
  callback?: () => void,
) => {
  if (step === "expand" && !customerInContract.value) {
    return;
  }
  const stage = step === "expand" ? "in-contract" : step === "dialog" ? "init" : "complete";
  const reason = step === "confirm" && realReason.value ? realReason.value : undefined;
  loading.value = true;
  try {
    await api!.deactivateMerchant(currentMerchantId, stage, { reason });
    if (step === "confirm") {
      await merchantsStore.initialize();
    }
    if (callback) {
      callback();
    }
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
};
const closeFinalStep = () => {
  setTimeout(() => (currentStep.value = Step.Confirm), 300); // So that users don't see the final step before the fade out
  isDialogActive.value = false;
};
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

div.v-window :deep(div.v-window__container) {
  display: flex;
  flex-grow: 1;
}
</style>
